import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getProtocols, createProtocol, executeProtocol, deleteProtocol, getProtocolLogs, extractErrorMessage } from '../services/api';
import { FaPlus, FaPlay, FaTrash, FaClipboardList, FaHistory } from 'react-icons/fa';
import { useToast } from '../components/Toast';
import '../styles/Protocols.css';

function Protocols({ user }) {
  const toast = useToast();
  const [protocols, setProtocols] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('protocols');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [newProtocol, setNewProtocol] = useState({
    name: '',
    description: '',
    steps: '',
    category: 'daily',
    required: false
  });

  useEffect(() => {
    fetchProtocols();
  }, []);

  const fetchProtocols = async () => {
    try {
      setLoading(true);
      const data = await getProtocols();
      setProtocols(data.protocols || data);
    } catch (error) {
      console.error('Error fetching protocols:', error);
      toast.error(extractErrorMessage(error, 'Protokolle konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async (protocolId) => {
    try {
      setLoading(true);
      const data = await getProtocolLogs(protocolId);
      setLogs(data.logs || data);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error(extractErrorMessage(error, 'Logs konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);
    try {
      await createProtocol(newProtocol);
      setNewProtocol({ name: '', description: '', steps: '', category: 'daily', required: false });
      setShowModal(false);
      toast.success('Protokoll erstellt');
      fetchProtocols();
    } catch (error) {
      console.error('Error creating protocol:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Erstellen des Protokolls'));
    } finally {
      setSaving(false);
    }
  };

  const handleExecute = async (id) => {
    try {
      await executeProtocol(id);
      toast.success('Protokoll ausgeführt');
      fetchProtocols();
      if (selectedProtocol && selectedProtocol.id === id) {
        fetchLogs(id);
      }
    } catch (error) {
      console.error('Error executing protocol:', error);
      toast.error(extractErrorMessage(error, 'Ausführung fehlgeschlagen'));
    }
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setFormError(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Protokoll wirklich löschen?')) return;
    try {
      await deleteProtocol(id);
      fetchProtocols();
      if (selectedProtocol && selectedProtocol.id === id) {
        setSelectedProtocol(null);
        setLogs([]);
      }
    } catch (error) {
      console.error('Error deleting protocol:', error);
    }
  };

  const handleViewLogs = (protocol) => {
    setSelectedProtocol(protocol);
    setActiveTab('logs');
    fetchLogs(protocol.id);
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Protokolle</h1>
          {user.isAdmin && (
            <button className="add-btn" onClick={() => setShowModal(true)}>
              <FaPlus /> Neues Protokoll
            </button>
          )}
        </div>

        <div className="filter-tabs">
          <button className={activeTab === 'protocols' ? 'active' : ''} onClick={() => setActiveTab('protocols')}>
            <FaClipboardList /> Protokolle
          </button>
          <button className={activeTab === 'logs' ? 'active' : ''} onClick={() => setActiveTab('logs')}>
            <FaHistory /> Ausführungslog
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : activeTab === 'protocols' ? (
          <div className="protocols-grid">
            {protocols.length > 0 ? (
              protocols.map(protocol => (
                <div key={protocol.id} className="protocol-card">
                  <div className="protocol-header">
                    <h3 className="protocol-name">{protocol.name}</h3>
                    <span className={`category-badge ${protocol.category}`}>{protocol.category}</span>
                  </div>
                  {protocol.description && <p className="protocol-desc">{protocol.description}</p>}
                  {protocol.steps && (
                    <div className="protocol-steps">
                      <span className="steps-label">Schritte:</span>
                      <p>{protocol.steps}</p>
                    </div>
                  )}
                  <div className="protocol-meta">
                    {protocol.required ? (
                      <span className="required-badge">Pflicht</span>
                    ) : (
                      <span className="optional-badge">Optional</span>
                    )}
                    {protocol.execution_count != null && (
                      <span className="exec-count">{protocol.execution_count}x ausgeführt</span>
                    )}
                  </div>
                  <div className="protocol-actions">
                    <button className="action-btn execute" onClick={() => handleExecute(protocol.id)} title="Ausführen">
                      <FaPlay /> Ausführen
                    </button>
                    <button className="action-btn logs" onClick={() => handleViewLogs(protocol)} title="Logs anzeigen">
                      <FaHistory />
                    </button>
                    {user.isAdmin && (
                      <button className="action-btn delete" onClick={() => handleDelete(protocol.id)} title="Löschen">
                        <FaTrash />
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-data">Keine Protokolle vorhanden</div>
            )}
          </div>
        ) : (
          <div className="logs-section">
            {selectedProtocol && (
              <div className="logs-header">
                <h3>Logs für: {selectedProtocol.name}</h3>
              </div>
            )}
            {logs.length > 0 ? (
              <div className="logs-list">
                {logs.map((log, idx) => (
                  <div key={log.id || idx} className="log-entry">
                    <div className="log-time">
                      {new Date(log.executed_at || log.created_at).toLocaleString('de-DE')}
                    </div>
                    <div className="log-details">
                      {log.executed_by && <span className="log-user">von {log.executed_by}</span>}
                      {log.notes && <p className="log-notes">{log.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">
                {selectedProtocol ? 'Keine Logs vorhanden' : 'Wähle ein Protokoll um Logs anzuzeigen'}
              </div>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neues Protokoll</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleCreate}>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={newProtocol.name}
                    onChange={e => setNewProtocol({...newProtocol, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    value={newProtocol.description}
                    onChange={e => setNewProtocol({...newProtocol, description: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Schritte</label>
                  <textarea
                    value={newProtocol.steps}
                    onChange={e => setNewProtocol({...newProtocol, steps: e.target.value})}
                    placeholder="Schritt-für-Schritt Anweisungen..."
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Kategorie</label>
                    <select
                      value={newProtocol.category}
                      onChange={e => setNewProtocol({...newProtocol, category: e.target.value})}
                    >
                      <option value="daily">Täglich</option>
                      <option value="morning">Morgens</option>
                      <option value="evening">Abends</option>
                      <option value="greeting">Begrüßung</option>
                      <option value="service">Service</option>
                      <option value="other">Sonstiges</option>
                    </select>
                  </div>
                  <div className="form-group checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={newProtocol.required}
                        onChange={e => setNewProtocol({...newProtocol, required: e.target.checked})}
                      />
                      Pflichtprotokoll
                    </label>
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal} disabled={saving}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={saving}>{saving ? 'Speichert…' : 'Erstellen'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Protocols;
